/**
 * @file Renders the contact-us entry point, a popover on desktop and a
 * modal on mobile, that opens the contact form.
 */
import { useState } from "react";
import { Popover, FloatButton, Form } from "antd";
import { MailOutlined, CloseOutlined } from "@ant-design/icons";

import { isMobile } from "@components/Constants";
import { useAppContext } from "@components/AppContext";
import type { AppContext } from "@components/AppContext/types";

import ContactForm from "./ContactForm";
import ContactUsMobile from "./ContactUsMobile";
import type { Props as ContactFormProps } from "./ContactForm";

import "./ContactUs.css";

const LABEL_TITLE = `We're here to help. Please explain to us the problem
you're facing and our team will get in touch with you as soon as possible.`;

const LABEL_CONTACT_US = "CONTACT US";

type Props = Omit<ContactFormProps, "form" | "request" | "onSuccess">;

/**
 * Renders the contact-us button and its form, as a popover on desktop or
 * a modal on mobile.
 */
const ContactUs = (props: Props): JSX.Element => {
  const [ form ] = Form.useForm();

  const [ isOpen, setIsOpen ] = useState(false);

  const { request } = useAppContext() as AppContext;

  /**
   * Closes the popover or modal and clears the form after a submission.
   */
  const onSuccess = () => {
    setIsOpen(false);
    form.resetFields();
  };

  /**
   * Toggles the popover or modal open state.
   */
  const onClick = () =>
    setIsOpen(!isOpen);

  const content = (
    <ContactForm
      form={form}
      request={request}
      onSuccess={onSuccess}
      {...props}
    />
  );

  if (isMobile) {
    return (
      <ContactUsMobile
        icon={<MailOutlined />}
        isOpen={isOpen}
        onClick={onClick}
      >
        {content}
      </ContactUsMobile>
    );
  }

  const type = isOpen
    ? "default"
    : "primary";

  const shape = "square";
  const title = <div style={{ maxWidth: 400 }}>{LABEL_TITLE}</div>;
  const placement = "topRight";
  const className = "contact-us";

  const icon = isOpen
    ? <CloseOutlined />
    : <MailOutlined />;

  return (
    <Popover
      open={isOpen}
      title={title}
      content={content}
      placement={placement}
    >
      <FloatButton
        icon={icon}
        type={type}
        shape={shape}
        onClick={onClick}
        className={className}
        description={LABEL_CONTACT_US}
      />
    </Popover>
  );
};

export default ContactUs;
